import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Label } from '@heroui/react';
import { CheckboxButtonGroup, Widget } from '@heroui-pro/react';
import type { UserRole } from '@/api/models';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { RECORDABLE_ROLES } from '../../operationEditorMeta';

type RolePickerProps = {
    roles: UserRole[];
    isReadOnly: boolean;
    onChange: (roles: UserRole[]) => void;
    /** Shown under the chips when the server refused the selection. */
    errorText: string | null;
};

/**
 * Who may record this operation.
 *
 * **A card in the right column, beside the preview** — not a per-pair property
 * and not a footer. It belongs with the preview because it is part of the same
 * thing: what publishing this version commits the tenant to. The recipe on the
 * left says how the entry is built; this says who is allowed to build one.
 *
 * The kit's `CheckboxButtonGroup`, laid out in a row: picking several of a
 * closed set of three is exactly what it models, and all three answers stay
 * visible, so a dropdown would hide the answer behind a click to save no space
 * at all.
 *
 * **OrgAdmin cannot be turned off.** The server adds it back whether or not it
 * was sent — an org admin who cannot record the operation they just configured
 * is a state nobody wants to debug — so the clearing toggle is swallowed rather
 * than letting the author deselect something that will silently come back. Not
 * a disabled card: greying out the one role that is always on reads as the
 * opposite of what is true. It looks exactly like the other selected answers,
 * with a padlock where its checkbox would be, and the caption underneath says
 * why — a control that ignores a click with no explanation reads as a bug.
 */
export const RolePicker: React.FC<RolePickerProps> = ({
    roles,
    isReadOnly,
    onChange,
    errorText,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Widget {...getNodeTestIdProp('operation-editor-roles')}>
            <Widget.Header>
                <Widget.Title>{t('operations.editor.rolesLabel')}</Widget.Title>
                <Widget.Description>
                    {t('operations.editor.rolesContract')}
                </Widget.Description>
            </Widget.Header>
            <Widget.Content className="flex flex-col gap-4">
                {/* One role per row, each spanning the card.
                    `CheckboxButtonGroup` exposes no orientation prop — its
                    `layout` variant only switches flex for grid, and the base
                    `.checkbox-button-group` is `display:flex` with no
                    direction, so a row is simply the flex default. Direction is
                    therefore a layout class, the same way `flex-row flex-wrap`
                    was before it. The items need no width of their own: a
                    column flex container stretches them. */}
                <CheckboxButtonGroup
                    className="flex-col"
                    isDisabled={isReadOnly}
                    isInvalid={errorText !== null}
                    name="rolesAllowed"
                    onChange={(next) => {
                        // Corrected, not swallowed: a freshly created
                        // template starts with no roles at all (not even
                        // OrgAdmin), so a swallow-if-missing guard would
                        // reject every click forever instead of just the one
                        // that tries to remove it.
                        const withOrgAdmin = next.includes('OrgAdmin')
                            ? next
                            : [...next, 'OrgAdmin'];
                        onChange(withOrgAdmin as UserRole[]);
                    }}
                    value={roles}
                >
                    {RECORDABLE_ROLES.map((role) => {
                        const locked = role.id === 'OrgAdmin';
                        return (
                            // `items-start` because the item centres its
                            // content by default — unnoticeable on the 128px
                            // cards of the old row, but a full-width row left
                            // a two-word label floating in the middle with the
                            // indicator pinned far right. Left-aligned, it
                            // reads as a row of answers and lines up with
                            // every other label in the editor.
                            //
                            // The end padding is the lane the indicator is
                            // absolutely positioned into, and it outlives the
                            // row layout: the indicator is still pinned to the
                            // card's top-right, so a long label would otherwise
                            // run under it.
                            <CheckboxButtonGroup.Item
                                className="items-start pe-12"
                                key={role.id}
                                value={role.id}
                                {...getNodeTestIdProp(
                                    `operation-editor-role-${role.id}`,
                                )}
                            >
                                <CheckboxButtonGroup.Indicator>
                                    {locked ? (
                                        <Icon
                                            aria-hidden
                                            icon="gravity-ui:lock"
                                        />
                                    ) : undefined}
                                </CheckboxButtonGroup.Indicator>
                                <CheckboxButtonGroup.ItemContent>
                                    <Label className="text-sm font-semibold">
                                        {t(role.labelKey)}
                                    </Label>
                                </CheckboxButtonGroup.ItemContent>
                            </CheckboxButtonGroup.Item>
                        );
                    })}
                </CheckboxButtonGroup>

                <p className="text-xs text-muted">
                    {t('operations.editor.rolesNote')}
                </p>

                {errorText ? (
                    <p
                        className="text-xs text-danger"
                        {...getNodeTestIdProp('operation-editor-roles-error')}
                    >
                        {errorText}
                    </p>
                ) : null}
            </Widget.Content>
        </Widget>
    );
};
